import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import isEmpty from "lodash/isEmpty";

import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Search } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

import { Translate, getTranslation } from "orion-components/i18n";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import ReminderDialog from "./components/ReminderDialog";
import Card from "./components/Card";
import { deleteReminders } from "./reminders/actions";
import { closeBulkNotifications } from "./notifications/actions";
import { getDir } from "orion-components/i18n/Config/selector";

const propTypes = {
	componentState: PropTypes.object
};

const defaultProps = {
	componentState: {}
};

const useStyles = makeStyles({
	formControlLabel: {
		fontSize: 10
	}
});

const NotificationsCenter = ({ componentState }) => {
	const classes = useStyles();
	const dispatch = useDispatch();

	const notifications = useSelector(state => Object.values(state.globalData?.notifications?.activeItemsById || {}));
	const reminders = useSelector(state => state.globalData?.reminders || []);
	const dir = useSelector(state => getDir(state));
	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));

	const [searchString, setSearchString] = useState("");
	const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
	const [editReminderPanel, setEditReminderPanel] = useState(false);
	const [reminderToEdit, setReminderToEdit] = useState(null);
	const [selectedReminders, setSelectedReminders] = useState([]);

	const data = useMemo(() => {
		const sourceData = editReminderPanel ? reminders : notifications;

		return [...sourceData].toSorted(
			(a, b) => new Date(b.createdDate || b.dueDate) - new Date(a.createdDate || a.dueDate)
		);
	}, [editReminderPanel, notifications, reminders]);

	const filteredData = useMemo(() => {
		if (isEmpty(searchString)) {
			return data;
		}

		const normalizedSearch = searchString.toLowerCase();

		return data.filter(item => {
			const searchableText = String(item.title || item.name || item.subject || "").toLowerCase();

			return searchableText.includes(normalizedSearch);
		});
	}, [data, searchString]);

	const visibleData = useMemo(
		() => filteredData.filter(item => (editReminderPanel ? item?.displayType : true)),
		[editReminderPanel, filteredData]
	);

	const selectableReminderIds = useMemo(
		() => filteredData.filter(item => item?.displayType).map(item => item.id),
		[filteredData]
	);

	const allRemindersSelected = useMemo(
		() => reminders.length > 0 && reminders.every(item => selectedReminders.includes(item.id)),
		[reminders, selectedReminders]
	);

	const searchNotification = useCallback(e => {
		setSearchString(e.target.value);
	}, []);

	const openReminderDialog = useCallback(() => {
		setReminderDialogOpen(true);
	}, []);

	const editReminder = useCallback(reminder => {
		setReminderToEdit(reminder);
		setReminderDialogOpen(true);
	}, []);

	const selectAllReminders = useCallback(
		e => {
			const checked = e.target.checked;
			const reminderIds = checked ? selectableReminderIds : [];
			setSelectedReminders(reminderIds);
		},
		[selectableReminderIds]
	);

	const closeDialog = useCallback(() => {
		setReminderDialogOpen(false);
		setReminderToEdit(null);
	}, []);

	const exitReminderEditMode = useCallback(() => {
		setSelectedReminders([]);
		setEditReminderPanel(false);
	}, []);

	const openReminderEditMode = useCallback(() => {
		setEditReminderPanel(true);
	}, []);

	const deleteSelectedReminders = useCallback(() => {
		if (selectedReminders.length) {
			dispatch(deleteReminders(selectedReminders));
		}
	}, [dispatch, selectedReminders]);

	const dismissAllNotifications = useCallback(() => {
		dispatch(closeBulkNotifications(notifications.map(notification => notification?.id)));
	}, [dispatch, notifications]);

	const styles = useMemo(
		() => ({
			wrapper: {
				overflow: scrollBarsEnabled ? "hidden" : "auto",
				height: "calc(100% - 48px)"
			},
			header: {
				display: "flex",
				width: "100%",
				padding: "0 12px 12px",
				alignItems: "center",
				height: 61
			},
			searchContainer: {
				width: "100%",
				display: "flex"
			},
			searchField: {
				backgroundColor: "transparent",
				width: "90%"
			},
			searchFieldSx: {
				"& .MuiInputBase-input": {
					height: "2.15rem !important"
				},
				"& input:-webkit-autofill": {
					boxShadow: "0 0 0 100px #2C2C2C inset",
					WebkitBoxShadow: "0 0 0 100px #2C2C2C inset",
					WebkitTextFillColor: "#6C6C6C"
				}
			},
			searchButton: {
				margin: "10px 0px 0px 10px"
			},
			searchIcon: {
				color: "6C6C6C",
				width: 24,
				height: 24
			},
			addReminderButton: {
				margin: 12,
				color: "#fff",
				backgroundColor: "#6EB4ED",
				height: 35
			},
			addReminderButtonSx: {
				"&:hover": {
					backgroundColor: "#3076AF !important"
				}
			},
			content: {
				height: "100%",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column"
			},
			actionsRow: {
				display: "flex",
				justifyContent: "flex-end",
				margin: "0 16px 0 13px"
			},
			headerAdornment: {
				fontSize: 10
			},
			cancelLink: {
				fontSize: 10,
				margin: "0px 10px",
				color: "#666C74"
			},
			deleteLink: {
				fontSize: 10,
				color: "#D3615C"
			},
			manageLink: {
				fontSize: 10,
				margin: "0px 10px"
			},
			checkbox: {
				color: "#B7BABF",
				...(dir === "rtl" ? { paddingRight: 0 } : { paddingLeft: 0 }),
				"&.Mui-checked": {
					color: "#1688bd!important"
				}
			},
			formControlLabel: {
				margin: 0,
				color: "#fff",
				...(dir === "rtl" ? { marginLeft: "auto" } : { marginRight: "auto" })
			},
			cardsContainer: {
				flex: 1,
				overflowY: "auto",
				paddingLeft: 13,
				...(!scrollBarsEnabled ? { paddingRight: 13 } : {})
			},
			card: {
				marginBottom: 10
			},
			emptyState: {
				padding: "24px 0px",
				color: "#B5B9BE",
				border: "1px solid #707070",
				borderRadius: "10px",
				height: "143px",
				margin: "23px 48px",
				display: "flex",
				alignItems: "center"
			},
			emptyStateSx: {
				color: "#98999D"
			},
			emptyStateText: {
				width: "100%",
				fontSize: 14,
				color: "#B4B8BC"
			}
		}),
		[dir, scrollBarsEnabled]
	);

	return (
		<div
			id="notification-tab-wrapper"
			style={styles.wrapper}
			className={"cf" + (componentState.hasError ? " adjusted" : "")}
		>
			<div style={styles.header}>
				<div className="search-field" style={styles.searchContainer}>
					<TextField
						id="search-field"
						placeholder={getTranslation("global.dock.notifications.main.search")}
						onChange={searchNotification}
						variant="standard"
						style={styles.searchField}
						sx={styles.searchFieldSx}
					/>
					<IconButton style={styles.searchButton}>
						<Search style={styles.searchIcon} />
					</IconButton>
				</div>
			</div>

			<Button
				variant="contained"
				color="primary"
				style={styles.addReminderButton}
				sx={styles.addReminderButtonSx}
				onClick={openReminderDialog}
			>
				<Translate value={"global.dock.notificationsCenter.addReminder"} />
			</Button>

			<div style={styles.content}>
				<div style={styles.actionsRow}>
					{editReminderPanel ? (
						<>
							<FormControlLabel
								control={
									<Checkbox
										checked={allRemindersSelected}
										onChange={selectAllReminders}
										color="primary"
										sx={styles.checkbox}
									/>
								}
								label={getTranslation("global.dock.notificationsCenter.selectAll")}
								style={styles.formControlLabel}
								classes={{ label: classes.formControlLabel }}
							/>
							<a
								onClick={exitReminderEditMode}
								className="cb-font-b2 cb-font-link"
								style={styles.cancelLink}
							>
								<Translate value="global.dock.notificationsCenter.cancel" />
							</a>
							<a
								onClick={deleteSelectedReminders}
								className={`cb-font-b2 cb-font-link ${selectedReminders?.length ? "" : "disabled"}`}
								style={styles.deleteLink}
							>
								<Translate value="global.dock.notificationsCenter.deleteSelected" />
							</a>
						</>
					) : (
						<>
							<a
								onClick={openReminderEditMode}
								className="cb-font-b2 cb-font-link"
								style={styles.manageLink}
							>
								<Translate value="global.dock.notificationsCenter.manageReminders" />
							</a>
							{filteredData?.length ? (
								<a
									onClick={dismissAllNotifications}
									className="cb-font-b2 cb-font-link"
									style={styles.headerAdornment}
								>
									<Translate value="global.dock.notificationsCenter.dismissAll" />
								</a>
							) : (
								<></>
							)}
						</>
					)}
				</div>
				<div style={styles.cardsContainer}>
					{filteredData?.length ? (
						visibleData.map(item => {
							return (
								<Card
									key={item.id}
									dir={dir}
									item={item}
									editMode={editReminderPanel}
									editReminder={editReminder}
									selectedReminders={selectedReminders}
									setSelectedReminders={setSelectedReminders}
									customStyles={styles.card}
								/>
							);
						})
					) : (
						<Typography style={styles.emptyState} align="center" variant="caption" sx={styles.emptyStateSx}>
							<Translate
								value={`global.dock.notifications.main.${editReminderPanel ? "noReminders" : "noNotifications"}`}
								style={styles.emptyStateText}
							/>
						</Typography>
					)}
				</div>
			</div>
			<ReminderDialog
				key={reminderToEdit ? reminderToEdit.id : "new-reminder"}
				open={reminderDialogOpen}
				closeDialog={closeDialog}
				dir={dir}
				reminder={reminderToEdit}
			/>
		</div>
	);
};

NotificationsCenter.propTypes = propTypes;
NotificationsCenter.defaultProps = defaultProps;

export default NotificationsCenter;
